import type { PaginationParams } from "../../tastyworks/request"
import type { ItemsResponse } from "../../tastyworks/response"
import type { RestResource } from "../common"

/**
 * A function that loops through all of the pages for a paginated response.
 * @param requestCallback a function that calls an HTTP endpoint
 * @param params additional parameters that need to be passed to the endpoint
 * @param otherArgs any additional arguments that need to be passed, e.g. accountNumber
 * @returns a promise that resolves to an array of the items
 */
export const fetchAllPages = async <T extends RestResource, P, A>(
  requestCallback: (
    args?: A,
    params?: P | PaginationParams
  ) => Promise<ItemsResponse<T>>,
  params: P | PaginationParams = {},
  ...otherArgs: A[]
) =>
  requestCallback(...otherArgs, params).then(async (firstResponse) => {
    const responsesArray: T[] = []
    responsesArray.push(...firstResponse.items)

    const {
      pagination: { totalPages },
    } = firstResponse
    if (totalPages > 1) {
      /**
       * Note [LW] - this currently calls the requests in sequence, waiting for each response to resolve before fetching
       * the next page. This was intentionally chosen over fetching all of the pages in parallel (and resolving with Promise.all).
       * This was to prevent cases where there are many pages that could trigger rate limiting for a user.
       * A potential improvement could be to batch the requests, but this wasn't necessary at the time of writing
       */
      for (let page = 2; page <= totalPages; page++) {
        const response = await requestCallback(...otherArgs, {
          ...params,
          pageOffset: page - 1,
        })
        responsesArray.push(...response.items)
      }

      return responsesArray
    }

    return responsesArray
  })
